// gti-chms-frontend/src/pages/AccountsListPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import AccountFormModal from '../../components/accounts/AccountFormModal'; 
import type { Account } from '../../models';




const AccountsListPage: React.FC = () => {
    const { user, authLoading } = useAuth();
    const [accounts, setAccounts] = useState<Account[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Modal States
    const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [accountToEdit, setAccountToEdit] = useState<Account | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
    const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);

    const fetchAccounts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            // The protect and authorize middleware on backend handles roles.
            // If the user isn't super-admin, the backend will return 403 Forbidden.
            const response = await api.get<Account[]>('/accounts');
            setAccounts(response.data);
        } catch (err: any) {
            console.error('Failed to fetch accounts:', err);
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching accounts.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Only fetch if user is authenticated and is a super-admin
        if (!authLoading && user && user.role === 'super-admin') {
            fetchAccounts();
        } else if (!authLoading && (!user || user.role !== 'super-admin')) {
            // If not super-admin, display an access denied message or redirect
            setError('You do not have permission to view this page.');
            setLoading(false);
        }
    }, [user, authLoading, fetchAccounts]); // Re-fetch when user/auth status changes

    // CRUD Handlers
    const handleSaveAccount = async (accountData: any, accountId?: string) => {
        try {
            if (accountId) {
                await api.put(`/accounts/${accountId}`, accountData);
            } else {
                await api.post('/accounts', accountData);
            }
            await fetchAccounts(); // Refresh the list
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            setAccountToEdit(null);
        } catch (err: any) {
            console.error("Error saving account:", err);
            throw err; // Re-throw to be caught by modal's internal error state
        }
    };

    const handleDeleteConfirm = async () => {
        if (!accountToDelete) return;
        try {
            await api.delete(`/api/accounts/${accountToDelete._id}`);
            await fetchAccounts(); // Refresh the list
            setIsDeleteModalOpen(false);
            setAccountToDelete(null);
        } catch (err: any) {
            console.error("Error deleting account:", err);
            setError(err.response?.data?.message || err.message || 'Failed to delete account.');
        }
    };

    // Modal Open/Close Handlers
    const openAddModal = () => setIsAddModalOpen(true);
    const closeAddModal = () => setIsAddModalOpen(false);

    const openEditModal = (account: Account) => {
        setAccountToEdit(account);
        setIsEditModalOpen(true);
    };
    const closeEditModal = () => {
        setAccountToEdit(null);
        setIsEditModalOpen(false);
    };

    const openDeleteModal = (account: Account) => {
        setAccountToDelete(account);
        setIsDeleteModalOpen(true);
    };
    const closeDeleteModal = () => {
        setAccountToDelete(null);
        setIsDeleteModalOpen(false);
    };


    if (loading || authLoading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
                <p className="ml-4 text-gray-700">Loading accounts...</p>
            </div>
        );
    }

    if (error && (!user || user.role !== 'super-admin')) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Access Denied!</strong>
                <span className="block sm:inline ml-2">{error}</span>
            </div>
        );
    }

    // Display access denied if user is not super-admin but the component is rendered
    if (!user || user.role !== 'super-admin') {
        return (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Permission Required!</strong>
                <span className="block sm:inline ml-2">You must be a Super Admin to manage accounts.</span>
            </div>
        );
    }


    return (
        <div className="bg-white p-6 rounded-lg shadow-md min-h-[calc(100vh-120px)] flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-semibold text-gray-800">Manage Accounts</h2>
                <button
                    onClick={openAddModal}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-150 ease-in-out"
                >
                    Add New Account
                </button>
            </div>

            {error && accounts.length > 0 && ( // Show error as banner if some accounts loaded
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">Error:</strong>
                    <span className="block sm:inline ml-2">{error}</span>
                </div>
            )}

            {accounts.length === 0 ? (
                <p className="text-gray-600 text-lg text-center mt-10">No accounts found for this church. Start by adding one!</p>
            ) : (
                <div className="overflow-x-auto flex-grow">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Provider
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Identifier
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Balance
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Purpose
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {accounts.map((account) => (
                                <tr key={account._id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {account.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {account.type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {account.provider}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {account.accountIdentifier}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-semibold">
                                        {account.currentBalance.toFixed(2)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {account.purpose || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${account.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {account.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                                        <button
                                            onClick={() => openEditModal(account)}
                                            className="text-indigo-600 hover:text-indigo-900 mr-4"
                                            title="Edit Account"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => openDeleteModal(account)}
                                            className="text-red-600 hover:text-red-900"
                                            title="Delete Account"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Account Add/Edit Modal */}
            <AccountFormModal
                isOpen={isAddModalOpen || isEditModalOpen}
                onClose={isAddModalOpen ? closeAddModal : closeEditModal}
                onSave={handleSaveAccount}
                accountToEdit={accountToEdit}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteConfirm}
                message={`Are you sure you want to delete account: "${accountToDelete?.name}" (${accountToDelete?.accountIdentifier})? This action cannot be undone.`}
                confirmText="Delete"
                isDestructive={true}
            />
        </div>
    );
};

export default AccountsListPage;
